import { Link } from 'react-router-dom';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { formatPhone, telHref } from '../widget-lib/phone-tel';
import { displayableMediaUrl } from '../widget-lib/media-url';
import { BranchDetailCard } from '../widget-ui/branch-detail-card';
import { RichTextBlock } from '../widget-ui/rich-text-block';
import { PATH } from '../widget-lib/path-routes';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';

function IconClock() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
    </svg>
  );
}

function IconPhone() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function IconChevronRight() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function tidyWorkingHours(raw: string | null): string {
  if (!raw) return '';
  return raw
    .replace(/ПН-ПТ/gi, 'Пн–Пт')
    .replace(/ПН‑ПТ/gi, 'Пн–Пт')
    .replace(/\bСБ\b/gi, 'Сб')
    .replace(/\bВС\b/gi, 'Вс')
    .replace(/—/g, '–');
}

export default function OrgAboutPage() {
  useWidgetSeo(
    'О компании',
    'Информация о компании, контакты, режим работы, адрес и филиалы.'
  );
  const { widgetConfig, guestShowcaseOrg } = useEmbedBootstrap();
  const suffix = useQuerySuffix();
  const cfg = widgetConfig ?? null;
  const orgName = cfg?.name?.trim() || guestShowcaseOrg?.name?.trim() || 'Организация';
  const description = cfg?.description?.trim() || null;
  const descriptionFormat = cfg?.description_format ?? 'plain';
  const workingHours = cfg?.working_hours?.trim() || null;
  const phone = cfg?.phone?.trim() || null;
  const photoUrl = displayableMediaUrl(cfg?.photo_url);
  const brandLogoUrl = displayableMediaUrl(cfg?.brand_logo_url);
  const branches = cfg?.branches ?? [];
  const typeLabel = (cfg?.organization_type_label ?? '').trim() || (cfg?.organization_type ?? '').trim() || null;

  const defaultTagline = 'Ваш островок спокойствия в центре города';

  return (
    <div className="flex flex-1 flex-col gap-6 px-4 pb-8 pt-4 sm:px-6 sm:pb-10 sm:pt-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-[var(--bg-elevated)] shadow-[var(--shadow-card)]">
        {photoUrl ? (
          <div className="relative h-44 w-full sm:h-56 lg:h-64">
            <img src={photoUrl} alt="" className="h-full w-full object-cover" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
          </div>
        ) : (
          <div className="flex h-44 w-full items-center justify-center sm:h-56 lg:h-64" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 50%, #93c5fd 100%)' }} />
        )}

        <div className="absolute bottom-0 left-0 right-0 px-3 pb-4 sm:px-4 sm:pb-5">
          <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 backdrop-blur-sm sm:gap-3 sm:px-4 sm:py-2 ${photoUrl ? 'bg-white/40' : 'bg-white/20'}`}>
            {brandLogoUrl ? (
              <img
                src={brandLogoUrl}
                alt=""
                className="h-8 w-8 shrink-0 rounded-full object-cover ring-2 ring-white/30 sm:h-10 sm:w-10"
              />
            ) : null}
            <div className="min-w-0">
              <h1 className="text-base font-light tracking-tight text-[var(--text)] sm:text-lg">{orgName}</h1>
              {typeLabel ? (
                <span className="inline-flex items-center rounded-full bg-black/30 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
                  {typeLabel}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* CTA — Записаться */}
      <Link
        to={`${PATH.services}${suffix}`}
        className="flex items-center justify-between rounded-2xl bg-[var(--accent-bg)] px-5 py-4 text-[var(--accent-fg)] shadow-sm transition-transform active:scale-[0.98] sm:px-6"
      >
        <div>
          <p className="text-base font-semibold">Записаться на сеанс</p>
          <p className="mt-0.5 text-sm text-[var(--accent-fg)]">Посмотрите услуги и выберите время</p>
        </div>
        <IconChevronRight />
      </Link>

      {/* О нас */}
      <section className="rounded-2xl bg-[var(--bg-elevated)] px-5 py-5 shadow-[var(--shadow-card)] sm:px-6 sm:py-6">
        <h2 className="mb-3 text-base font-semibold text-[var(--text)]">О нас</h2>
        {description ? (
          <RichTextBlock
            html={description}
            format={descriptionFormat}
            className="text-sm leading-relaxed text-[var(--text-muted)]"
          />
        ) : (
          <p className="text-sm leading-relaxed text-[var(--text-muted)]">{defaultTagline}</p>
        )}
      </section>

      {/* Контакты */}
      <section className="rounded-2xl bg-[var(--bg-elevated)] px-5 py-5 shadow-[var(--shadow-card)] sm:px-6 sm:py-6">
        <h2 className="mb-4 text-base font-semibold text-[var(--text)]">Контакты</h2>

        <div className="space-y-3">
          {workingHours ? (
            <div className="flex items-center gap-3">
              <IconClock />
              <div className="min-w-0 flex-1">
                <RichTextBlock html={tidyWorkingHours(workingHours)} format="markdown" className="text-sm text-[var(--text)]" />
              </div>
            </div>
          ) : null}

          {phone ? (
            <div className="flex items-center gap-3">
              <IconPhone />
              <div className="min-w-0 flex-1">
                <a href={telHref(phone)} className="text-sm text-[var(--accent)] underline-offset-2 hover:underline">
                  {formatPhone(phone)}
                </a>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* Филиалы */}
      {branches.length > 0 ? (
        <section>
          <h2 className="mb-3 text-base font-semibold text-[var(--text)]">Наши студии</h2>
          <ul className="space-y-3">
            {branches.map((b) => (
              <BranchDetailCard
                key={b.id}
                branchId={b.id}
                name={b.name}
                address={b.address}
                workingHours={b.working_hours}
                phone={b.phone}
                organizationId={cfg?.organization_id}
                timezone={cfg?.timezone}
                pickBranch={false}
              />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
